import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { map } from 'rxjs/operators';
import { WorkspaceBase } from './workspaces.state';

/**
 * Full workspace data shown on the detail page.
 */
export interface WorkspaceDetailData extends WorkspaceBase {
  organizationId: string;
  caseTypeId: string;
  caseTypeName: string;
  updatedAt: Date;
  usedStorage: number;
  config?: Record<string, unknown>;
  numMembers: number;
  numCases: number;
}

export interface WorkspaceDetailStateModel {
  id: string | undefined;
  organizationId: string | undefined;
  data: WorkspaceDetailData | undefined;
  loading: boolean;
  error: string | undefined;
  deleting: boolean;
  deletionError: string | undefined;
}

@Injectable()
export class WorkspaceDetailState extends ComponentStore<WorkspaceDetailStateModel> {
  constructor() {
    super({
      id: undefined,
      organizationId: undefined,
      data: undefined,
      loading: false,
      error: undefined,
      deleting: false,
      deletionError: undefined,
    });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly id$ = this.select((state) => state.id);
  public readonly organizationId$ = this.select((state) => state.organizationId);
  public readonly data$ = this.select((state) => state.data);
  public readonly loading$ = this.select((state) => state.loading);
  public readonly error$ = this.select((state) => state.error);
  public readonly deleting$ = this.select((state) => state.deleting);
  public readonly deletionError$ = this.select((state) => state.deletionError);

  public readonly numCases$ = this.data$.pipe(map((data) => data?.numCases ?? 0));
  public readonly numMembers$ = this.data$.pipe(map((data) => data?.numMembers ?? 0));
  public readonly caseTypeName$ = this.data$.pipe(map((data) => data?.caseTypeName ?? ''));

  // ============================================================================
  // Updaters
  // ============================================================================

  public readonly setData = this.updater(
    (state, payload: { id: string; organizationId: string; data: WorkspaceDetailData }) => ({
      ...state,
      id: payload.id,
      organizationId: payload.organizationId,
      data: payload.data,
      loading: false,
      error: undefined,
    })
  );

  public readonly mergeData = this.updater((state, changes: Partial<WorkspaceDetailData>) => {
    if (!state.data) return state;
    return {
      ...state,
      data: { ...state.data, ...changes },
    };
  });

  public readonly setLoading = this.updater((state, loading: boolean) => ({ ...state, loading }));

  public readonly setError = this.updater((state, error: string | undefined) => ({
    ...state,
    error,
    loading: false,
  }));

  public readonly setDeleting = this.updater((state, deleting: boolean) => ({ ...state, deleting }));

  public readonly setDeletionError = this.updater((state, deletionError: string | undefined) => ({
    ...state,
    deletionError,
    deleting: false,
  }));

  public readonly clear = this.updater(() => ({
    id: undefined,
    organizationId: undefined,
    data: undefined,
    loading: false,
    error: undefined,
    deleting: false,
    deletionError: undefined,
  }));
}
